// Copy and display metadata for the Automation rules page. Values and their allowed
// options come from the server (FinanceAutomationRule.effective_row serves both), so
// nothing here duplicates a threshold or an option list — only the words around them.
//
// Copy rule for a signal card: the title, description and threshold sentence say what the
// rule DETECTS, never what happens next. The outcome belongs to the single action card, and
// a detector that names an outcome is what made the old page describe a system that doesn't
// exist (each rule looked like it could refund on its own; none of them can).

export const PAGE_COPY = {
  back: 'Back to flagged orders',
  title: 'Automation rules',
  subtitle:
    'Decide what Opendate does on its own when an order looks fraudulent — and what it brings to you instead.',
  loading: 'Loading…',
  loadError: 'Could not load automation rules. Please refresh.',
  saveError: 'Could not save that change. Please try again.',
  // A 403 from the write gate, not a transient failure — retrying will fail identically, so
  // the message says what actually changed underneath the tab.
  saveDenied:
    "Opendate's fraud automation is no longer switched on for your venue, so that change wasn't saved. Refresh to see the current settings.",
  comingSoon: 'Coming soon',
  // Shown to every team whose rollout flag is off — which on day one is all but the pilot.
  // Without it the Coming soon pills read as a broken page rather than a release in
  // progress, and a venue can't tell "no suspicious orders" from "not switched on yet".
  deferredLead: "Opendate's own fraud signals aren't switched on for your venue yet.",
  // "None of the signals above" rather than "nothing below": this notice renders above the
  // block-list rule, which is ungated and ships ON, and above the daily digest, which is
  // never gated. Only Opendate's own signal rules are held back.
  deferredBody:
    'We’re releasing them one venue at a time. None of the signals above are running today — flagged orders still arrive from Stripe and land in your queue, and refunds stay yours to make. The rules here are a preview of what’s coming.',
};

// Three steps, not the design's four: step 3a was "refunded automatically", which cannot
// happen while unattended refunds are switched off. Add it back with them.
//
// `icon` is a name, not a component: this file stays copy-only, and AutomationIcon owns the
// name → SVG mapping (as it does for every card icon below).
export const HOW_IT_WORKS_STEPS = [
  {
    eyebrow: 'Step 1',
    title: 'An order comes in',
    body: 'Every ticket purchase is checked as it happens.',
    icon: 'order',
  },
  {
    eyebrow: 'Step 2',
    title: 'Signals look at it',
    body: "The card issuer's own alerts, plus the Opendate signals you've turned on below.",
    icon: 'signals',
  },
  {
    eyebrow: 'Step 3',
    title: 'Everything goes to review',
    body: 'Lands in flagged orders. You decide, nothing happens to the fan yet.',
    icon: 'review',
  },
];

export const SECTIONS = {
  issuer: {
    id: 'issuer-alerts',
    title: 'Alerts from the card issuer',
    subtitle:
      "The strongest signal there is — the bank telling us a card was used without permission. This isn't a guess on our part, so it can act on its own.",
  },
  signals: {
    id: 'opendate-signals',
    title: 'Opendate fraud signals',
    subtitle:
      'Patterns we spot across the Opendate network. These are strong hints, not proof — so you decide what they add up to.',
  },
  afterRefund: {
    id: 'after-refund',
    title: 'After you refund an order as fraud',
    subtitle:
      'What happens to the buyer once an order is refunded — whether Opendate did it automatically or you did it by hand.',
  },
  notifications: {
    id: 'your-notifications',
    title: 'Your notifications',
    subtitle: 'Settings that affect you only. Other people on your team set their own.',
  },
};

// No toggle. PaymentReview.upsert_efw_from_stripe! runs straight off the
// radar.early_fraud_warning webhook, consulting no rule and no team flag, so issuer alerts
// reach the queue whether efw_auto_refund is on or off — the rule only ever gated the
// refund. A flag-only switch here would be inert, so the card states what already happens.
export const ISSUER_COPY = {
  ruleType: 'efw_auto_refund',
  title: 'The bank reports the card as stolen',
  icon: 'issuer',
  badge: 'Recommended',
  description: 'Stripe passes along an early fraud warning from the card issuer.',
  standsAlone:
    'These always land in flagged orders — the bank has already confirmed the fraud, so this signal stands on its own and doesn’t need corroboration from the Opendate signals below.',
  refundLead: 'Refunding and voiding the tickets automatically — coming soon.',
  refundBody: 'Until then, the refund is yours to make.',
  // Shown once unattended refunds are available. The card still has no toggle of its own at
  // that point — phase 2 owes it a real control plus the safety-window select — so this states
  // what the team would need to do rather than implying a switch exists here.
  refundAvailableLead: 'Refunding and voiding the tickets automatically is now available.',
  refundAvailableBody:
    'Turn it on for the issuer alert rule to have Opendate refund these without you.',
};

export const SIGNAL_RULES = [
  {
    ruleType: 'network_signal',
    title: 'Same card buying across many events',
    icon: 'events',
    badge: 'Recommended',
    description: 'Catches scalpers spreading purchases across shows on the Opendate network.',
    tooltip: {
      term: 'how cards are matched',
      text: "Matched on the card fingerprint — Stripe's anonymous ID for a card. We never see or store the full card number.",
    },
    threshold: {
      key: 'min_events',
      before: 'Matches when one card is used on',
      after: 'in the last 90 days.',
      // "upcoming" because the rule counts only shows that are still active
      // (PaymentReviewSignals.network_counts active_only: true), while the evidence row on
      // the flagged order shows the unnarrowed 90 days. Without the word, an order whose
      // evidence reads "8 events" can fail to trip a threshold of 5 with nothing on the
      // page explaining the gap.
      label: (value) => `${value} or more upcoming events`,
      adjustLabel: 'Adjust the events threshold',
      guidance: 'Lowering this catches more, but sweeps up superfans and season-ticket buyers.',
    },
  },
  {
    ruleType: 'evasion_signal',
    title: 'One card, different names, same show',
    icon: 'ticket',
    badge: null,
    description:
      'Someone getting around your per-order ticket limit by splitting the buy across fake names.',
    tooltip: null,
    threshold: {
      // Key stays min_orders to match the column the money path reads; only the copy
      // talks about names, which is what evasion_counts actually counts.
      key: 'min_orders',
      before: 'Matches when',
      after: 'share one card on a single event.',
      label: (value) => `${value} or more names`,
      adjustLabel: 'Adjust the names threshold',
      guidance:
        'At 2, couples and families buying on one card will match. Most venues sit at 3.',
    },
  },
  {
    ruleType: 'score_threshold',
    title: 'Stripe thinks the payment looks risky',
    icon: 'score',
    badge: null,
    description:
      'Stripe scores every payment for fraud risk. This catches the ones it rates highest.',
    tooltip: {
      term: 'how Stripe scores a payment',
      text: 'Known in Stripe as the Radar risk score, 0–99. It weighs things like the buyer’s location, device, and card history.',
    },
    threshold: {
      key: 'min_score',
      // "reaches", not "is above": FraudRuleEvaluator compares with >=, so a threshold of 75
      // matches a score of exactly 75. "Above" would misread by one to anyone tuning it.
      before: "Matches when Stripe's risk score reaches",
      after: 'out of 99.',
      label: (value) => `${value}`,
      adjustLabel: 'Adjust the risk score threshold',
      guidance:
        "Stripe blocks anything it's certain about before it reaches you. This is the grey area underneath that.",
    },
  },
];

// Derived rather than hand-listed, so membership and order can't drift from SIGNAL_RULES.
export const SIGNAL_RULE_TYPES = SIGNAL_RULES.map((rule) => rule.ruleType);

// The single place an outcome is decided. Explanatory only while unattended refunds are
// off: it writes nothing, because the one reachable option is already what happens.
export const ACTION_COPY = {
  title: 'The action',
  icon: 'action',
  description: 'One decision, covering every signal above.',
  groupLabel: 'What happens when a signal matches',
  flag: {
    value: 'flag',
    title: 'Always send to review',
    body: 'Nothing gets refunded without you. Every match lands in flagged orders and waits for a person.',
  },
  autoRefund: {
    value: 'auto_refund',
    title: 'Refund automatically when signals agree',
    body: 'One signal on its own is never enough. An order is only refunded when several match at once — anything less goes to review.',
  },
  // Read only by assistive tech: with no radio semantics there is no checked state to
  // announce, so the live outcome has to say so in words.
  activeLabel: 'This is what happens today.',
  summaryEyebrow: 'Right now',
  summaryLink: 'See what’s been flagged',
};

// Singular/plural and "either one" vs "any of them" are why this is one function rather
// than template soup in JSX — and why it has its own unit test.
export const signalSummary = (enabledCount, totalCount, deferred) => {
  if (deferred) {
    return 'None of these signals are switched on for your venue yet. When they are, every match will land in flagged orders — the refund is always yours to make.';
  }
  if (enabledCount === 0) {
    return "No signals are on. Opendate isn't watching for these patterns yet. Turn one on above and matching orders will land in flagged orders.";
  }
  if (enabledCount === 1) {
    return `1 of ${totalCount} signals is on. An order that trips it lands in flagged orders for you to decide.`;
  }
  const count =
    enabledCount === totalCount
      ? `All ${totalCount} signals are on.`
      : `${enabledCount} of ${totalCount} signals are on.`;
  const which = enabledCount === 2 ? 'either one' : 'any of them';
  return `${count} An order that trips ${which} lands in flagged orders for you to decide.`;
};

// "until Opendate lifts it", not the design's "until you remove it": PaymentReviews::BlockList
// only ever writes into Stripe Radar value lists. There is no local record, no endpoint and
// no UI to take an entry back off, so promising the team can is a promise we'd break.
export const BLOCK_LIST_COPY = {
  ruleType: 'block_list_on_refund',
  title: 'Stop that card and email from buying again',
  icon: 'block',
  description:
    'The card and email are added to your block list, so future orders from them are declined at checkout.',
  onLead: 'A block stays in place until Opendate lifts it.',
  onBody:
    "If a real fan gets caught by mistake, they won't be able to buy from you again until it's removed.",
  offLead: "Turning the rule off stops new blocks — it doesn't lift the ones already there.",
  offBody: 'Anyone already blocked stays blocked.',
};

// No count: blocks live in Stripe Radar value lists with no local mirror, so there is
// nothing to count without a live Stripe read. Renders with the button disabled rather than
// hidden, so a team can see the capability exists and how to reach it today.
//
// Deliberately does NOT tell teams to open Stripe themselves: BlockList#add_item writes
// through team.stripe_api_options, and non-Connect teams share the platform account behind a
// per-team alias, so they have no dashboard of their own to look in.
export const BLOCKED_BUYERS_COPY = {
  title: 'Blocked buyers',
  icon: 'buyers',
  body: 'Every block is written to a Stripe list called “Opendate fraud”. Seeing who’s on it — and taking someone off — is coming to Opendate. Until then, contact Opendate support to lift a block.',
  action: 'Review list',
};

// "daily", not the design's "morning": the digest is a single 06:20 UTC fan-out with no
// per-venue timezone bucketing (config/schedule.yml), which is 2:20am ET and 11:20pm PT the
// previous evening. Also drops "any refunds taken overnight" — none can happen yet.
export const DIGEST_COPY = {
  ruleType: 'daily_risk_digest',
  title: 'Email me a daily risk summary',
  icon: 'digest',
  badge: 'Only you',
  description: 'The orders flagged in the last day, in one email.',
};
